import { inject, Injectable, resource, computed } from "@angular/core";
import { firstValueFrom, filter, take, combineLatest } from "rxjs";
import { toObservable } from "@angular/core/rxjs-interop";
import { IframeMessagesService } from "@shared/iframe-messages-service";
import { ResponseErrorHandlerService } from "@shared/response-error-handler-service";
import { AuthService, type CurrentUser } from "@shared/auth";

export interface IiFirmDeliveryMethod {
    Id: number;
    Sequence: number;
    IsDefault: boolean;
    InUse: boolean;
    CanDelete: boolean;
    DisplayName: string;
    "en-CA": string;
    "fr-CA": string;
}

export interface DeliveryMethodsResource {
    user: CurrentUser | null;
}

@Injectable({ providedIn: "root" })
export class IFirmDeliveryMethodsService {
    // iFirm ships "None" as a regular delivery method rather than leaving the field empty, so it is the
    // value that stands for "nothing selected" on iFirm dashboards.
    public static readonly NONE_DELIVERY_METHOD_ID = 1;

    private readonly authService = inject(AuthService);
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);

    private deliveryMethodsResource = resource<IiFirmDeliveryMethod[] | undefined, DeliveryMethodsResource>({
        params: () => ({
            user: this.authService.currentUser(),
        }),
        loader: ({ params }) => {
            const { user } = params;
            if (!user) return Promise.resolve([]);

            return this.loadiFirmDeliveryMethods();
        },
    });
    private deliveryMethodsResourceStatus$ = toObservable(this.deliveryMethodsResource.status);
    public currentDeliveryMethods = computed(() => this.deliveryMethodsResource.value());
    private currentDeliveryMethods$ = toObservable(this.currentDeliveryMethods);

    private async loadiFirmDeliveryMethods(): Promise<IiFirmDeliveryMethod[]> {
        try {
            const result = await this.iframeMessagesService.postMessageResp("getiFirmDeliveryMethods", {}, "gotiFirmDeliveryMethods");
            if (result.status === "success") return result.deliveryMethods as IiFirmDeliveryMethod[];
        } catch {
            console.error("Failed to load iFirm delivery methods");
        }

        this.responseErrorHandlerService.showPopup({
            typeLayout: "ERROR",
            messageCode: "defaultError",
        });
        return [];
    }

    public async getiFirmDeliveryMethods(): Promise<IiFirmDeliveryMethod[]> {
        await this.waitForDeliveryMethods();
        return this.currentDeliveryMethods() ?? [];
    }

    // Do not use it. Only use in getiFirmDeliveryMethods() for fallback compatibility. It will be removed in the future.
    private async waitForDeliveryMethods(): Promise<void> {
        await firstValueFrom(
            combineLatest([this.deliveryMethodsResourceStatus$, this.currentDeliveryMethods$]).pipe(
                filter(
                    ([status, currentDeliveryMethods]) =>
                        status !== "loading" && status !== "reloading" && currentDeliveryMethods !== undefined && currentDeliveryMethods !== null,
                ),
                take(1),
            ),
        );
    }
}
